
import React, { useEffect } from 'react';
import { CheckCircle, AlertCircle, X, Info } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info';

export interface ToastMessage {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
}

interface ToastProps {
  toast: ToastMessage | null;
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ toast, onClose }) => {
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        onClose();
      }, 4000); // Auto close after 4 seconds
      return () => clearTimeout(timer);
    }
  }, [toast, onClose]);

  if (!toast) return null;

  const bgColors = {
    success: 'bg-white border-l-4 border-green-500',
    error: 'bg-white border-l-4 border-red-500',
    info: 'bg-white border-l-4 border-blue-500',
  };

  const iconColors = {
    success: 'text-green-500',
    error: 'text-red-500',
    info: 'text-blue-500',
  };

  const Icons = {
    success: CheckCircle,
    error: AlertCircle,
    info: Info,
  };

  const Icon = Icons[toast.type];

  return (
    <div className="fixed top-6 right-0 left-0 md:left-auto md:right-6 z-[100] flex justify-center md:justify-end pointer-events-none px-4">
      <div 
        className={`${bgColors[toast.type]} shadow-xl rounded-lg p-4 pr-10 relative pointer-events-auto transform transition-all duration-300 animate-fade-in-up min-w-[300px] max-w-md flex items-start gap-3`}
        role="alert"
      >
        <Icon className={`${iconColors[toast.type]} mt-0.5 flex-shrink-0`} size={20} />
        <div>
          <h4 className="font-bold text-gray-800 text-sm">{toast.title}</h4>
          {toast.message && <p className="text-gray-600 text-sm mt-1">{toast.message}</p>}
        </div>
        <button 
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default Toast;
